import { CopyLlmConfigMode, CopySchedulerFlowLlmConfigInput } from 'types/apollo/globalTypes';

/** The original template's LLM config, as the metadata panel classifies it. */
export type SourceLlmConfig =
  | { kind: 'none' }
  | { kind: 'legacy' }
  | { kind: 'shared'; agentConfigId: string };

export type LlmConfigChoice = 'attach-same' | 'attach-other' | 'clone-to-shared' | 'migrate-source';

/** Mirrors the LLM Configuration panel on the outreach template page. Null: not an LLM agent. */
export const getSourceLlmConfig = (metadata: {
  nlpAgentId: string | null | undefined;
  agentConfigurationId: string | null | undefined;
  hasLlmConfig: boolean | null | undefined;
  hasPrompt: boolean | null | undefined;
}): SourceLlmConfig | null => {
  if (!metadata.nlpAgentId?.startsWith('llm_')) return null;
  if (metadata.agentConfigurationId) {
    return { kind: 'shared', agentConfigId: metadata.agentConfigurationId };
  }
  if (metadata.hasLlmConfig && metadata.hasPrompt) return { kind: 'legacy' };
  return { kind: 'none' };
};

/** The first entry is the default. No option creates a legacy config: new configs are shared only. */
export const llmConfigChoicesFor = (source: SourceLlmConfig | null): LlmConfigChoice[] => {
  switch (source?.kind) {
    case 'shared':
      return ['attach-same', 'attach-other', 'clone-to-shared'];
    case 'legacy':
      return ['migrate-source', 'clone-to-shared'];
    default:
      return [];
  }
};

export const createsNewConfig = (choice: LlmConfigChoice | undefined) =>
  choice === 'clone-to-shared' || choice === 'migrate-source';

/**
 * `<base> - Copy N` with the lowest N not already taken, case-insensitively. A copy of a copy
 * keeps counting ("X - Copy 2") instead of stacking suffixes.
 */
export const nextCopyName = (name: string, takenNames: string[]) => {
  const base = name.trim().replace(/ - Copy \d+$/i, '');
  const taken = new Set(takenNames.map((n) => n.trim().toLowerCase()));
  for (let n = 1; ; n++) {
    const candidate = `${base} - Copy ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
};

/**
 * The migrated config's name: the original template's, or `<name> - N (from duplicated OT)` with
 * the lowest free N once that is taken (the plain name counts as the first).
 */
export const migratedConfigName = (templateName: string, takenNames: string[]) => {
  const base = templateName.trim();
  const taken = new Set(takenNames.map((n) => n.trim().toLowerCase()));
  if (!taken.has(base.toLowerCase())) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base} - ${n} (from duplicated OT)`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
};

export type NameCheck = { error: string; warning: string };

/** An exact match blocks; a match differing only in case is allowed with a warning. */
export const checkName = (name: string, takenNames: string[], label: string): NameCheck => {
  const trimmed = name.trim();
  if (!trimmed) return { error: '', warning: '' };
  if (takenNames.some((n) => n.trim() === trimmed)) {
    return { error: `This name is already taken by another ${label}`, warning: '' };
  }
  const similar = takenNames.find((n) => n.trim().toLowerCase() === trimmed.toLowerCase());
  return {
    error: '',
    warning: similar
      ? `Another ${label} is named "${similar.trim()}" (different capitalization)`
      : '',
  };
};

export const buildLlmConfigInput = ({
  source,
  choice,
  otherConfigId,
  newConfigName,
  newConfigDescription,
}: {
  source: SourceLlmConfig | null;
  choice: LlmConfigChoice | undefined;
  otherConfigId: string;
  newConfigName: string;
  newConfigDescription: string;
}): CopySchedulerFlowLlmConfigInput | null => {
  if (!choice || !llmConfigChoicesFor(source).includes(choice)) return null;

  const newConfig = {
    newConfigName: newConfigName.trim(),
    newConfigDescription: newConfigDescription.trim(),
  };

  switch (choice) {
    case 'attach-same':
      return source?.kind === 'shared'
        ? { mode: CopyLlmConfigMode.ATTACH, agentConfigId: source.agentConfigId }
        : null;
    case 'attach-other':
      return { mode: CopyLlmConfigMode.ATTACH, agentConfigId: otherConfigId };
    case 'clone-to-shared':
      return { mode: CopyLlmConfigMode.CLONE_TO_SHARED, ...newConfig };
    case 'migrate-source':
      return { mode: CopyLlmConfigMode.MIGRATE_SOURCE_TO_SHARED, ...newConfig };
  }
};
